import { useEffect, useState, CSSProperties } from "react";
import { useNavigate } from "react-router-dom";
import { View } from "tamagui";
import { containerStyle } from "@styles/index";
import { colors } from "@styles/colors";
import { formatDate } from "@utils/func/timeFormatter";
import { PRACTICE_ADDRESS_FULL, practiceGasStatusExpiring } from "@utils/practice/practiceOrder";
import { practiceSupplierName } from "@utils/practice/practiceMerchant";

/**
 * 練習情境「即將到期通知下單」的模擬 LINE 聊天室（路由 /practice-notice）。
 * --------------------------------------------------------------------------
 * 客戶在真實 LINE 會收到瓦斯行推播的「瓦斯快用完」flex 卡片，點卡片上的按鈕進 LIFF 下單。
 * 練習站沒有（也不該有）Messaging API，因此在這裡把那則卡片與聊天室外觀原樣畫出來，
 * 讓客服看得到「客戶手機上長什麼樣、要按哪一顆」，按下去就接進既有的一鍵下單流程。
 *
 * 卡片上的日期與首頁同一份來源（practiceGasStatusExpiring），客服切到首頁對照時不會兜不起來。
 * 版面用原生 div：這頁刻意模仿 LINE 自己的 UI，不套 App 的元件樣式。
 */

/** 距今天幾天（正 = 未來、負 = 過去）；輸入為 YYYY-MM-DD。 */
const daysFromToday = (date: string): number => {
  const target = new Date(`${date}T00:00:00`);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / 86400000);
};

const LINE_BG = "#8CABD8";
const CARD_W = 300;

const cardBtn = (primary: boolean): CSSProperties => ({
  width: "100%",
  border: primary ? "none" : `1px solid ${colors.border || "#E2E5EB"}`,
  background: primary ? "#1B6EC2" : "#fff",
  color: primary ? "#fff" : "#4B5563",
  borderRadius: 8,
  padding: "11px 8px",
  fontSize: 15,
  fontWeight: 800,
  cursor: "pointer",
});

const LineNotice = () => {
  const navigate = useNavigate();
  const [shown, setShown] = useState(false);
  const [replayKey, setReplayKey] = useState(0);

  // 訊息「剛送達」的進場動畫：進頁面稍等一下再滑入，接近真實推播的感覺。
  useEffect(() => {
    setShown(false);
    const t = window.setTimeout(() => setShown(true), 350);
    return () => window.clearTimeout(t);
  }, [replayKey]);

  const gas = practiceGasStatusExpiring();
  const daysLeft = Math.max(0, daysFromToday(gas.depletion_low));
  const sinceLast = Math.abs(daysFromToday(gas.last_order_date));
  const now = new Date();
  const clock = `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;

  return (
    <View {...containerStyle} height="100dvh" width="100vw" background={LINE_BG}>
      <div style={{ flex: 1, minHeight: 0, height: "100%", display: "flex", flexDirection: "column", overflowY: "auto" }}>
        {/* 模擬 LINE 聊天室標頭 */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "12px 14px",
            background: "#FFFFFF",
            borderBottom: "1px solid rgba(0,0,0,0.08)",
            flexShrink: 0,
          }}>
          <span style={{ fontSize: 20, color: "#4B5563" }}>‹</span>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: 15,
              background: "#06C755",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 15,
            }}>
            🔥
          </div>
          <div style={{ fontSize: 15.5, fontWeight: 800, color: "#111827" }}>{practiceSupplierName()}</div>
        </div>

        {/* 練習說明條（含重播鈕：放在頂部才不會被底部教練面板蓋住） */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "8px 14px",
            background: "rgba(255,255,255,0.85)",
            flexShrink: 0,
          }}>
          <div style={{ flex: 1, fontSize: 12, color: "#475569", lineHeight: 1.5 }}>
            這是<b>模擬的 LINE 聊天室</b>：瓦斯快用完時，客戶手機會收到下面這張提醒卡。
          </div>
          <button
            type="button"
            onClick={() => setReplayKey((k) => k + 1)}
            style={{
              flexShrink: 0,
              border: `1px solid ${colors.border || "#E2E5EB"}`,
              background: "#fff",
              color: "#334155",
              borderRadius: 999,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 700,
              cursor: "pointer",
            }}>
            🔁 重播
          </button>
        </div>

        {/* 對話區 */}
        <div style={{ flex: 1, padding: "16px 14px 190px" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
            <span style={{ background: "rgba(0,0,0,0.18)", color: "#fff", fontSize: 11.5, borderRadius: 999, padding: "3px 12px" }}>今天</span>
          </div>

          <div
            key={replayKey}
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 8,
              opacity: shown ? 1 : 0,
              transform: shown ? "translateY(0)" : "translateY(14px)",
              transition: "opacity .35s ease, transform .35s ease",
            }}>
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 17,
                background: "#06C755",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 17,
                flexShrink: 0,
              }}>
              🔥
            </div>

            {/* flex 卡片本體（對應正式環境推播的 bubble） */}
            <div style={{ width: CARD_W, background: "#fff", borderRadius: 14, overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,0.18)" }}>
              {/* header */}
              <div style={{ padding: "16px 16px 10px" }}>
                <div style={{ fontSize: 19, fontWeight: 900, color: colors.amber }}>🔔 瓦斯快用完了</div>
                <div style={{ fontSize: 12.5, color: "#6B7280", marginTop: 5, lineHeight: 1.5 }}>{PRACTICE_ADDRESS_FULL}</div>
              </div>

              {/* body */}
              <div style={{ padding: "0 16px 14px" }}>
                <div style={{ fontSize: 16.5, fontWeight: 900, color: "#111827", lineHeight: 1.45 }}>
                  預估 {formatDate(gas.depletion_low)} ～ {formatDate(gas.depletion_high)} 用完
                </div>
                <div style={{ fontSize: 13, color: colors.amber, fontWeight: 800, marginTop: 3 }}>距離預估用完約剩 {daysLeft} 天</div>

                <div style={{ background: "#F7F7F7", borderRadius: 10, padding: 11, marginTop: 11, display: "grid", gap: 6 }}>
                  {[
                    ["上次叫桶", `${formatDate(gas.last_order_date)}（${sinceLast} 天前）`],
                    ["平均用量", `約 ${gas.predicted_days} 天一桶`],
                    ["建議叫桶", `${formatDate(gas.suggested_delivery_date)} 前`],
                  ].map(([k, v]) => (
                    <div key={k} style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
                      <span style={{ fontSize: 12.5, color: "#6B7280" }}>{k}</span>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: "#374151" }}>{v}</span>
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: 12, color: "#E67E22", marginTop: 9, lineHeight: 1.5 }}>
                  ⚠️ 假日與傍晚時段較滿，建議提早一兩天預約。
                </div>
              </div>

              {/* footer：正式環境這兩顆是 LIFF 連結，練習站直接接進對應畫面 */}
              <div style={{ padding: "0 12px 12px", display: "grid", gap: 8 }}>
                <button type="button" style={cardBtn(true)} onClick={() => navigate("/kiosk")}>
                  立即叫瓦斯
                </button>
                <button type="button" style={cardBtn(false)} onClick={() => navigate("/home")}>
                  查看使用天數
                </button>
              </div>
            </div>

            <span style={{ fontSize: 11, color: "rgba(255,255,255,0.9)", flexShrink: 0 }}>{clock}</span>
          </div>
        </div>
      </div>
    </View>
  );
};

export default LineNotice;
